import { Tooltip } from 'react-tooltip';
import { tooltipClasses } from 'configurator-ui-shared';

import { ContainedButton } from '../../../components/buttons';
import { SHOPIFY_PRODUCT_TAG } from '../../../config';
import { formatCentsToDollarString } from '../../../utils';
import { useConfiguratorDispatch, useRootSelector } from '../../../store';
import { useGetProductQuery } from '../../api';
import {
  findMultiConfigAddonVariant,
  findMultiConfigIncludedVariant
} from '../../cart/utils';
import {
  configureAnother,
  selectActiveConfigSnapshot,
  selectConfigIds,
  selectIsAtConfigCap,
  selectMaxConfigs
} from '../store/multiConfigSlice';
import { getConfigNameValue } from '../utils';

interface ConfigureAnotherButtonProps {
  productTag?: string;
}

export function ConfigureAnotherButton({
  productTag
}: ConfigureAnotherButtonProps) {
  const dispatch = useConfiguratorDispatch();
  const isAtCap = useRootSelector(selectIsAtConfigCap);
  const maxConfigs = useRootSelector(selectMaxConfigs);
  const activeConfigSnapshot = useRootSelector(selectActiveConfigSnapshot);
  const configIds = useRootSelector(selectConfigIds);
  const { data: apiProduct } = useGetProductQuery(
    productTag || SHOPIFY_PRODUCT_TAG
  );

  if (isAtCap) {
    return (
      <p className="mt-2 text-sm text-gray-500">
        You&apos;ve reached the maximum of {maxConfigs} names.
      </p>
    );
  }

  // Block until the current configuration has a name, so shoppers can't
  // rack up several indistinguishable "Name N" entries at once.
  const hasName = !!getConfigNameValue(
    activeConfigSnapshot?.properties
  )?.trim();

  // Mirrors resolveConfigVariant's 3-tier rule for whichever configuration
  // this button would create next (always index >= 1, since this button
  // only ever shows once a first configuration already exists).
  const nextIndex = configIds.length;
  const pricingThreshold = apiProduct?.multi_config_pricing_threshold ?? 2;
  const nextVariant =
    nextIndex + 1 < pricingThreshold
      ? findMultiConfigIncludedVariant(apiProduct?.multi_config_included_sku)
      : findMultiConfigAddonVariant(apiProduct?.multi_config_addon_sku);
  // Only the upsells-collection lookup carries price data - the
  // base-product-variant fallback (see findMultiConfigAddonVariant) doesn't,
  // so the price suffix simply doesn't show in that case rather than
  // guessing.
  const nextPriceCents =
    nextVariant && 'price' in nextVariant ? nextVariant.price : undefined;

  return (
    <>
      {/* Rendered inline (not portaled) - unlike the dropdown's tooltip,
          nothing near this button claims an elevated z-index, so it has
          no local stacking conflict to escape. */}
      <Tooltip
        id="configure-another-tooltip"
        className={tooltipClasses}
        delayShow={1000}
      />
      <ContainedButton
        size="large"
        color="secondary"
        className="mt-3"
        style={{ border: '1px solid currentColor' }}
        onClick={() => dispatch(configureAnother())}
        disabled={!hasName}
        data-testid="configure-another-button"
        data-tooltip-id="configure-another-tooltip"
        data-tooltip-content={hasName ? '' : 'Input text to add a name'}
        data-tooltip-place="top"
      >
        Add Another Name
        {typeof nextPriceCents === 'number' &&
          ` (+${formatCentsToDollarString(nextPriceCents)})`}
      </ContainedButton>
    </>
  );
}
